/**
 * Paso 5 del alta: Deuda inicial (ORR-443)
 * La sección del lote arma sus filas con `armarDeudasDeLote`, a partir de los servicios que
 * cargó el paso de Servicios y del período elegido acá. Las filas del lote son las mismas que
 * generaría el lote mensual: mismo concepto, mismo monto. Las deudas sueltas las carga el usuario
 * con un servicio de periodicidad única.
 */
import { useEffect, useMemo, useState } from 'react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MonthPicker } from '@/components/ui/month-picker'
import { Receipt, Plus, Trash2 } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { usePermissions } from '@/hooks/usePermissions'
import { armarDeudasDeLote, type ServicioDeLote } from '@/services/honorariosService'
import { getPeriodoActual } from '@/components/movimientos/forms/movimientoSchema'
import type { ClienteForm } from '../useClienteForm'
import type { DeudaAltaValues } from '../clienteSchema'
import type { Servicio } from '@/types/database'

// Mismo default que usa el lote mensual (`GenerarLoteDialog`), para que el concepto de una deuda
// cargada acá no se distinga de una generada después por el lote.
const CONCEPTO_LOTE_DEFAULT = 'Honorarios {servicio} - {periodo}'

/**
 * Reconstruye el período y las filas excluidas del lote a partir de `deudasIniciales`, que escribe
 * este paso. El wizard monta un paso por vez, así que al volver acá el paso arranca de cero.
 * El período sale de las filas mensuales. Una fila del lote de ese período sin su deuda mensual
 * quedó excluida. Si el campo ya fue escrito y no tiene filas mensuales, quedaron excluidos todos
 * los servicios del lote y el período vuelve al mes corriente.
 */
const leerEleccionPrevia = (form: ClienteForm, servicios: ServicioDeLote[], nombreCliente: string) => {
  const mensuales = (form.state.values.deudasIniciales ?? []).filter((d) => d.tipo_deuda === 'mensual')
  const periodo = mensuales[0]?.mes_anio ?? getPeriodoActual()
  if (mensuales.length === 0 && !form.getFieldMeta('deudasIniciales')?.isTouched) {
    return { periodo, excluidos: new Set<string>() }
  }
  const incluidos = new Set(mensuales.map((d) => d.servicio_id))
  const candidatos =
    mensuales.length > 0
      ? armarDeudasDeLote({ servicios, mesAnio: periodo, conceptoTemplate: CONCEPTO_LOTE_DEFAULT, nombreCliente })
      : servicios
  return { periodo, excluidos: new Set(candidatos.map((c) => c.servicio_id).filter((id) => !incluidos.has(id))) }
}

interface StepDeudaInicialProps {
  form: ClienteForm
  /** Catálogo de servicios de la organización. Acá salen los de periodicidad 'unico' para las deudas sueltas. */
  servicios: Servicio[]
}

export function StepDeudaInicialNew({ form, servicios }: StepDeudaInicialProps) {
  const { puede } = usePermissions()
  const [deudasSueltas, setDeudasSueltas] = useState<DeudaAltaValues[]>(
    () => (form.state.values.deudasIniciales ?? []).filter((d) => d.tipo_deuda === 'esporadica')
  )

  const nombreCliente =
    [form.state.values.nombre, form.state.values.apellido].filter(Boolean).join(' ') || 'el cliente'

  const serviciosParaLote: ServicioDeLote[] = useMemo(
    () =>
      (form.state.values.serviciosConPrecio ?? []).map((s) => ({
        servicio_id: s.servicio_id,
        precio: s.precio,
        activo: true,
        frecuencia: s.frecuencia,
        mes_inicio: s.mes_inicio ?? 1,
        servicio_nombre: servicios.find((sv) => sv.id === s.servicio_id)?.nombre ?? 'Servicio',
      })),
    [form.state.values.serviciosConPrecio, servicios]
  )

  const [eleccionPrevia] = useState(() => leerEleccionPrevia(form, serviciosParaLote, nombreCliente))
  const [periodo, setPeriodo] = useState(eleccionPrevia.periodo)
  const [excluidos, setExcluidos] = useState(eleccionPrevia.excluidos)

  const filasLote = useMemo(
    () =>
      armarDeudasDeLote({
        servicios: serviciosParaLote,
        mesAnio: periodo,
        conceptoTemplate: CONCEPTO_LOTE_DEFAULT,
        nombreCliente,
      }),
    [serviciosParaLote, periodo, nombreCliente]
  )

  const deudasLoteIncluidas = useMemo(
    () => filasLote.filter((f) => !excluidos.has(f.servicio_id)) as DeudaAltaValues[],
    [filasLote, excluidos]
  )

  const serviciosUnicos = useMemo(
    () => servicios.filter((s) => s.estado === 'activo' && s.periodicidad === 'unico'),
    [servicios]
  )

  // La RPC exige crearMovimiento para cualquier deuda, y sin editar+ver honorarios el paso
  // anterior no cargó servicios. Misma condición que gatea la sección del lote más abajo.
  const mostrarLote = Boolean(puede.editarHonorariosCliente && puede.verHonorariosCliente && puede.crearMovimiento)

  // El campo del form se deriva del estado, no de los handlers: así llega sincronizado aunque el
  // usuario nunca toque un checkbox ni el período (el lote ya viene armado por default) y aunque
  // vuelva al paso de servicios y cambie algo (`deudasLoteIncluidas` se recalcula solo).
  useEffect(() => {
    form.setFieldValue('deudasIniciales', [...(mostrarLote ? deudasLoteIncluidas : []), ...deudasSueltas])
  }, [deudasLoteIncluidas, deudasSueltas, mostrarLote, form])

  const toggleFila = (servicioId: string, incluir: boolean) => {
    setExcluidos((prev) => {
      const next = new Set(prev)
      if (incluir) next.delete(servicioId)
      else next.add(servicioId)
      return next
    })
  }

  const cambiarPeriodo = (value: string | null) => {
    setPeriodo(value ?? getPeriodoActual())
  }

  const agregarSuelta = () => {
    setDeudasSueltas((prev) => [
      ...prev,
      { servicio_id: '', concepto: '', monto: 0, tipo_deuda: 'esporadica', mes_anio: null },
    ])
  }

  const actualizarSuelta = (index: number, cambios: Partial<DeudaAltaValues>) => {
    setDeudasSueltas((prev) => prev.map((d, i) => (i === index ? { ...d, ...cambios } : d)))
  }

  const quitarSuelta = (index: number) => {
    setDeudasSueltas((prev) => prev.filter((_, i) => i !== index))
  }

  const totalLote = deudasLoteIncluidas.reduce((sum, d) => sum + d.monto, 0)
  const totalSueltas = deudasSueltas.reduce((sum, d) => sum + d.monto, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-2 border-b">
        <div className="p-2 rounded-lg bg-primary/10">
          <Receipt className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-medium">Deuda inicial</h3>
          <p className="text-sm text-muted-foreground">La deuda que el cliente ya trae al alta</p>
        </div>
      </div>

      {/* Lote: exige ver y editar honorarios (sin eso no hay servicios cargados en el paso
          anterior) y crearMovimiento, que la RPC pide para cualquier deuda. */}
      {mostrarLote && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <Label className="text-base font-semibold">Deuda del lote</Label>
            <MonthPicker value={periodo} onValueChange={cambiarPeriodo} />
          </div>
          {filasLote.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ningún servicio del paso anterior corresponde facturar en este período.
            </p>
          ) : (
            <div className="rounded-md border divide-y">
              {filasLote.map((fila) => (
                <label
                  key={fila.servicio_id}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={!excluidos.has(fila.servicio_id)}
                    onCheckedChange={(checked) => toggleFila(fila.servicio_id, checked === true)}
                  />
                  <span className="flex-1">{fila.concepto}</span>
                  <span className="font-medium">{formatCurrency(fila.monto)}</span>
                </label>
              ))}
            </div>
          )}
          {deudasLoteIncluidas.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
              <span className="text-sm font-medium text-muted-foreground">Total del lote</span>
              <span className="text-lg font-bold text-primary">{formatCurrency(totalLote)}</span>
            </div>
          )}
        </div>
      )}

      {/* Sueltas: solo con permiso de cargar movimientos. */}
      {puede.crearMovimiento && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Otras deudas</Label>
            <Button type="button" variant="outline" size="sm" onClick={agregarSuelta}>
              <Plus className="h-4 w-4 mr-1" /> Agregar deuda
            </Button>
          </div>
          {deudasSueltas.map((deuda, index) => (
            <div key={index} className="flex items-start gap-2 rounded-md border p-3">
              <div className="flex-1 space-y-2">
                <Select
                  value={deuda.servicio_id}
                  onValueChange={(value) => {
                    const servicio = serviciosUnicos.find((s) => s.id === value)
                    actualizarSuelta(index, {
                      servicio_id: value,
                      concepto: servicio ? `${servicio.nombre} - ${periodo}` : deuda.concepto,
                      monto: servicio?.precio_estimado || deuda.monto,
                    })
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Servicio único" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviciosUnicos.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={deuda.concepto}
                  onChange={(e) => actualizarSuelta(index, { concepto: e.target.value })}
                  placeholder="Concepto"
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={deuda.monto || ''}
                  onChange={(e) => actualizarSuelta(index, { monto: parseFloat(e.target.value) || 0 })}
                  placeholder="Monto"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 mt-1"
                onClick={() => quitarSuelta(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {deudasSueltas.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border">
              <span className="text-sm font-medium text-muted-foreground">Total de otras deudas</span>
              <span className="text-lg font-bold">{formatCurrency(totalSueltas)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
